import { Injectable, inject, signal } from '@angular/core';

import { CompanyApiService } from 'src/app/api/services/company-api.service';
import { CompanyTreeService } from 'src/app/app-common/services/company-tree.service';
import { CompanyNode } from 'src/app/app-common/types/company-tree';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import {
  ScrollingListFactoryService,
  ScrollingListInstance,
  ScrollingListSearch,
} from 'src/app/components/scrolling-list/services/scrolling-list-factory.service';
import { AddressUtilsService } from 'src/app/shared/services/address-utils.service';
import { CompanyTreeItem } from 'src/app/settings/services/company';

const SEARCH_PAGE_SIZE = 40;

type SearchCompany = Pick<
  CompanyTreeItem,
  | 'id'
  | 'name'
  | 'parentId'
  | 'parentName'
  | 'street'
  | 'city'
  | 'province'
  | 'hasSubcompanies'
>;

@Injectable()
export class CompanyTreeSearchService {
  private readonly companyApi = inject(CompanyApiService);
  private readonly treeService = inject(CompanyTreeService);
  private readonly companyState = inject(CompanyStateService);
  private readonly addressUtils = inject(AddressUtilsService);
  private readonly scrollingListFactory = inject(ScrollingListFactoryService);

  readonly search: ScrollingListSearch;

  private readonly list: ScrollingListInstance<SearchCompany>;
  private readonly queryState = signal('');
  private readonly resultsState = signal<CompanyNode[]>([]);
  private readonly loadingState = signal(false);
  private readonly errorMessageState = signal<string | null>(null);
  private requestedQuery: string | null = null;

  readonly query = this.queryState.asReadonly();
  readonly results = this.resultsState.asReadonly();
  readonly loading = this.loadingState.asReadonly();
  readonly errorMessage = this.errorMessageState.asReadonly();

  constructor() {
    this.search = {
      // Read per search, never here: this service is constructed before the
      // tree service is initialized, so the user company is still unknown.
      companyId: null,
      includeSubcompanies: true,
      query: '',
      sortBy: 'name',
      count: SEARCH_PAGE_SIZE,
      skipFirstLoad: true,
      name: 'Companies',
      onListUpdate: () => this.syncFromList(),
    };
    this.list = this.scrollingListFactory.create(
      this.companyApi.getCompanies.bind(this.companyApi),
      this.search,
    );
  }

  doSearch = (): void => {
    const rootId = this.companyState.getUserCompanyId();
    this.search.companyId = rootId;
    const query =
      typeof this.search.query === 'string' ? this.search.query : '';
    this.queryState.set(query);

    if (!query || !rootId) {
      this.requestedQuery = null;
      this.resetResults();
      return;
    }

    this.treeService.clearSearchExpansion();
    this.loadingState.set(true);
    this.errorMessageState.set(null);

    // Cleared on dispatch, not on settlement: a superseded query's results
    // must never linger under a new one, and this is the only place a new
    // first page starts. `load()` never reaches here, so paging is unaffected.
    this.resultsState.set([]);

    // The list factory ignores a load while one is already running, so a query
    // issued now would never reach the server. `syncFromList` re-issues it.
    if (this.list.loadingItems) {
      return;
    }

    this.requestedQuery = query;
    this.list.doSearch();
  };

  load = (): void => {
    if (!this.queryState() || !this.search.companyId) {
      return;
    }

    this.list.load();
    this.loadingState.set(this.list.loadingItems);
  };

  private reissuePendingSearch(): void {
    const rootId = this.companyState.getUserCompanyId();
    this.search.companyId = rootId;
    const pending = this.queryState();
    if (!pending || !rootId) {
      return;
    }

    this.search.query = pending;
    this.requestedQuery = pending;
    this.treeService.clearSearchExpansion();
    this.loadingState.set(true);
    this.errorMessageState.set(null);
    this.list.doSearch();
  }

  private resetResults(): void {
    this.list.items.clear();
    this.list.errorMessage = '';
    this.list.apiError = '';
    this.list.errorCodes = [];
    this.loadingState.set(false);
    this.errorMessageState.set(null);
    this.resultsState.set([]);
    this.treeService.clearSearchExpansion();
  }

  private syncFromList(): void {
    // A response can still land after the query was cleared: `doSearch` resets
    // the state but cannot cancel a request already in flight, and the factory
    // repopulates `list.items` when it settles. Discard that response instead
    // of restoring results — and a stale error — for an abandoned query.
    if (!this.queryState() || !this.search.companyId) {
      this.requestedQuery = null;
      this.resetResults();
      return;
    }

    const userCompanyId = this.companyState.getUserCompanyId();

    // A query typed while a response was still in flight supersedes it; the
    // settled response answers a query nobody is looking at any more.
    if (!this.list.loadingItems && this.requestedQuery !== this.queryState()) {
      this.reissuePendingSearch();
      return;
    }

    // The user's own company is the tree's own first row, so a result
    // repeating it would duplicate the row the search replaced.
    const hits = this.list.items.list.filter(
      (item) => item?.id && item.id !== userCompanyId,
    );

    // The rule `loadBranch` applies: `null` and absent are "not told", not a
    // declared leaf, so a server without the flag keeps the optimistic chevron.
    const declaredLeaves = hits
      .filter((item) => item.hasSubcompanies === false)
      .map((item) => item.id);
    this.treeService.markKnownLeaves(declaredLeaves);

    const results = hits.map((item) => this.toResult(item));

    this.resultsState.set(results);
    this.loadingState.set(this.list.loadingItems);
    this.errorMessageState.set(this.list.apiError ? this.list.apiError : null);
  }

  private toResult(company: SearchCompany): CompanyNode {
    const result: CompanyNode = {
      id: company.id ?? '',
      name: company.name ?? '',
      parentId: company.parentId ?? null,
      address: this.addressUtils.formatShortAddress(company),
    };

    const parentName =
      typeof company.parentName === 'string' ? company.parentName.trim() : '';
    if (parentName) {
      result.parentName = parentName;
    }

    return result;
  }
}
